import React, { memo, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { LocalStorageKey } from 'utils/localStorage';
import RoutePath from 'App/RoutePath';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import useGetUserTraits from 'hooks/useGetUserTraits';
import { tracking } from 'App/Tracking';
import { AuthStatus, LoginAction, authStatusMap } from 'App/Auth';
import { identifyBrazeUserAndMergeProfiles } from 'App/braze/identifyBrazeUser';
import { useApolloClient } from '@apollo/client';
import useFeatureFlagUserContext from 'flags/useFeatureFlagUserContext';
import { configureSentryUserScope, logException } from 'App/logging';
// moment-timezone, not moment: `moment.tz` is the plugin's, and importing the
// plain package leaves it dependent on something else having loaded the plugin.
import * as moment from 'moment-timezone';
import useUpdateUser from 'Account/hooks/useUpdateUser';
import { UserFeature } from '__generated__/globalTypes';
import { useConfirmUserAccount } from './hooks/useConfirmUserAccount';
import { TypedRegistrationFlow } from './Forms/RegistrationFlow/TypedRegistrationFlow';
import { useAccountCreationTracking } from './Forms/RegistrationFlow/hooks/useAccountCreationTracking';
import {
  RegistrationFlow,
  AccountCreationSubRoutes,
} from './Forms/RegistrationFlow/constants';
import { RegistrationFlowState } from './Forms/RegistrationFlow/types';
import { useSubdomainInfo } from './useSubdomainInfo';
import { useValidateRegistrationSession } from './hooks/useValidateRegistrationSession';

const redirectAfterSuccess = ({
  history,
}: {
  history: ReturnType<typeof useHistory>;
}) => {
  history.push(RoutePath.Home, { isFirstLogin: true });
};

const storeUserToken = (token: string) => {
  localStorage.setItem(LocalStorageKey.TOKEN, token);
};

export const Registration = memo(() => {
  const history = useHistory();
  const { search } = useLocation();

  const { updateUser, loading: updateUserLoading } = useUpdateUser();

  const subdomain = getSubdomainFromUrl();
  const { hasDepartmentOrLocationConfig, loggedOutFeatures } = useSubdomainInfo(
    {
      subdomain,
    },
  );

  const showWorkDetails =
    hasDepartmentOrLocationConfig &&
    loggedOutFeatures?.includes(UserFeature.DEPARTMENT_LOCATION_SELECTION);
  const {
    validateRegistrationSession,
    loading: validateRegistrationSessionLoading,
  } = useValidateRegistrationSession();

  const [registrationToken, setRegistrationToken] = useState<string | null>(
    null,
  );

  const {
    getUserTraits,
    error: traitsError,
    loading: traitsLoading,
  } = useGetUserTraits();
  const { identifyFeatureFlagUser } = useFeatureFlagUserContext();

  const client = useApolloClient();

  const { onStepViewed, onStepCompleted } = useAccountCreationTracking();

  const trackAndIdentifyUser = async () => {
    try {
      const userTimezone = moment.tz.guess();
      const result = await updateUser({
        timezone: userTimezone,
      });
      if (result.error) {
        logException(result.error);
      }

      const traits = await getUserTraits();
      const userId = traits?.uuid;

      configureSentryUserScope({
        id: userId || '',
        subdomain,
      });

      if (traitsError || !traits || !userId) {
        return;
      }

      tracking.identifyUser({
        userId,
        traits,
      });
      tracking.track('login-successful', {
        subdomain,
        isFirstLogin: true,
        loginAction: LoginAction.REGISTRATION,
        type: authStatusMap[AuthStatus.SUCCESS],
      });

      // This call is made in the backend also. We're duplicating it to avoid missing any users that may be uploaded to Braze in the interim.
      await identifyBrazeUserAndMergeProfiles(client, userId);

      void identifyFeatureFlagUser?.({
        userId,
        subdomain,
        locationId: traits.locationId,
        departmentId: traits.departmentId,
      });
    } catch (error) {
      // Allow login to continue even if we fail to track or identify the user
      logException(error);
    }
  };

  useEffect(() => {
    const id = new URLSearchParams(search).get('registrationToken');

    if (id && !registrationToken) {
      void validateRegistrationSession({ registrationToken: id });
      setRegistrationToken(id);
    }

    if (!id && !registrationToken) {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSuccess = async ({ token }: { token: string }) => {
    storeUserToken(token);
    await trackAndIdentifyUser();
    redirectAfterSuccess({ history });
  };

  const {
    loading: confirmUserAccountLoading,
    confirmUserAccount,
    error,
  } = useConfirmUserAccount({
    onSuccess,
  });

  const onAccountCreationFlowEnd = async (
    userDetails: RegistrationFlowState[RegistrationFlow.ACCOUNT_CREATION],
  ) => {
    const input = {
      registrationToken,
      userData: {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        password: userDetails.password,
        ...(userDetails.location && { locationId: userDetails.location }),
        ...(userDetails.department && { departmentId: userDetails.department }),
      },
      consentGiven: {
        marketing: userDetails.marketingOptIn,
        explicitPrivacy: userDetails.explicitPrivacyConsentGiven,
        explicitEEADataStorage: userDetails.explicitEEADataStorageConsentGiven,
        explicitHealthData: true,
        // Undefined unless shown, and undefined variables are omitted.
        piplPrivacyPolicyConsentGiven:
          userDetails.piplPrivacyPolicyConsentGiven,
        piplSensitiveDataConsentGiven:
          userDetails.piplSensitiveDataConsentGiven,
        piplThirdPartySharingConsentGiven:
          userDetails.piplThirdPartySharingConsentGiven,
        piplCrossBorderTransferConsentGiven:
          userDetails.piplCrossBorderTransferConsentGiven,
      },
    };

    await confirmUserAccount(input);
  };

  const skippedSubroutes = !showWorkDetails
    ? [AccountCreationSubRoutes.WorkDetails]
    : [];

  const loading =
    confirmUserAccountLoading ||
    traitsLoading ||
    validateRegistrationSessionLoading ||
    updateUserLoading;

  return (
    <TypedRegistrationFlow
      loading={loading}
      registrationFlow={RegistrationFlow.ACCOUNT_CREATION}
      onEnd={onAccountCreationFlowEnd}
      error={error}
      parentPath={RoutePath.Register}
      skippedSubroutes={skippedSubroutes}
      onStepViewed={onStepViewed}
      onStepCompleted={onStepCompleted}
    />
  );
});

export default Registration;
